import { queryOptions } from '@tanstack/react-query';

import { fetchReferenceAssistantGateway } from './fetchReferenceAssistantGateway';
import { type CitationRequest, isInFlightStatus } from '../model/citationRequest';
import type { ReferenceAssistantGateway } from '../ports/referenceAssistantGateway';

import { FIVE_MINUTES, ONE_MINUTE, THREE_SECONDS } from '@/constants/time';
import { userScoped } from '@/util/userScope';

/**
 * How often the conversation-scoped citation-request list refetches
 * while a search is in flight. The realtime `messageUpdated` path is the
 * primary completion signal; this poll is the fallback for a dropped or
 * missed realtime event (app backgrounded, reconnect) so the wizard still
 * detects `awaiting_review` and advances.
 */
const CITATION_REQUEST_POLL_INTERVAL_MS = THREE_SECONDS;

/**
 * Age cap on the in-flight poll, measured from a request's `createdAt`.
 * Past this the request is treated as hung and polling stops, so a
 * workflow that never terminates can't keep the list refetching forever.
 */
const MAX_CITATION_SEARCH_POLL_WINDOW_MS = FIVE_MINUTES;

/**
 * Poll only while some request is still in flight AND young enough to
 * plausibly still be running. Takes `now` explicitly so it can be tested
 * without a live `Query`.
 */
export function pickCitationRequestPollInterval(
  requests: readonly CitationRequest[] | undefined,
  now: number
): number | false {
  if (!requests || requests.length === 0) {
    return false;
  }

  const hasFreshInFlight = requests.some(
    (request) =>
      isInFlightStatus(request.status) &&
      now - Date.parse(request.createdAt) < MAX_CITATION_SEARCH_POLL_WINDOW_MS
  );

  return hasFreshInFlight ? CITATION_REQUEST_POLL_INTERVAL_MS : false;
}

/** Every conversation's citation requests. */
export const CITATION_REQUESTS_ROOT_KEY = ['citationRequests'] as const;

/**
 * A conversation's citation requests. The key is pinned by
 * `queryOptions.keys.test.ts`: the realtime handler invalidates it and the
 * wizard patches it by key.
 */
export const citationRequestsQueryKey = (conversationId: string) =>
  [...CITATION_REQUESTS_ROOT_KEY, conversationId] as const;

export function getCitationRequestsByConversationV2QueryOptions({
  conversationId,
  enabled = true,
  gateway = fetchReferenceAssistantGateway,
}: {
  conversationId: string;
  enabled?: boolean;
  gateway?: ReferenceAssistantGateway;
}) {
  return queryOptions({
    queryKey: citationRequestsQueryKey(conversationId),
    queryFn: () => gateway.listCitationRequests(conversationId),
    enabled: enabled && Boolean(conversationId),
    staleTime: ONE_MINUTE,
    gcTime: FIVE_MINUTES,
    refetchInterval: (query) => pickCitationRequestPollInterval(query.state.data, Date.now()),
    /**
     * The dropped-realtime-event case this poll rescues, a backgrounded web
     * tab, is exactly when focus is lost, so keep polling unfocused.
     */
    refetchIntervalInBackground: true,
  });
}

/** Every conversation's statement references. */
export const STATEMENT_REFERENCES_ROOT_KEY = ['statementReferences'] as const;

/**
 * Every account's statement references for a conversation: the prefix the
 * realtime handler invalidates, since an announcement names the
 * conversation and not who is looking at it.
 */
export const statementReferencesQueryKey = (conversationId: string) =>
  [...STATEMENT_REFERENCES_ROOT_KEY, conversationId] as const;

/**
 * The reference-assistant state of every expert message in a conversation.
 * Scoped to the account because the author alone sees citation intent and
 * help requests. Realtime announcements invalidate it, so it is never
 * polled.
 */
export const getStatementReferencesV2QueryOptions = userScoped(
  'user',
  (
    userId: string | undefined,
    conversationId: string,
    gateway: ReferenceAssistantGateway = fetchReferenceAssistantGateway
  ) =>
    queryOptions({
      queryKey: [...statementReferencesQueryKey(conversationId), userId] as const,
      queryFn: () => gateway.listStatementReferences(conversationId),
      enabled: Boolean(conversationId),
      staleTime: ONE_MINUTE,
      gcTime: FIVE_MINUTES,
    })
);
